<template>
    <!-- Show Welcome component for unauthenticated users -->
    <Welcome v-if="!isAuthenticated"/>

    <!-- Dashboard for authenticated users -->
    <div v-else class="home-page">
        <div class="page-container">
            <!-- STATE A: Company Selection Screen -->
            <div v-if="!selectedGlobalCompany" class="company-selection-screen">
                <div class="selection-card">
                    <div class="selection-icon">
                        <font-awesome-icon icon="fa-solid fa-chart-line" style="font-size: 80px; color: #0ea5e9;" />
                    </div>
                    <h1 class="selection-title">{{ $t('welcome.heroTitle') }}</h1>
                    <p class="selection-subtitle">{{ $t('companies.selectCompany') }}</p>

                    <div class="selection-selector">
                        <CompanySelector
                            v-model="tempCompanyId"
                            :placeholder="$t('companies.selectCompany')"
                            :show-label="false"
                            @change="handleCompanySelection"
                        />
                    </div>

                    <p class="selection-hint">
                        {{ $t('companies.noCompanyYet') }}
                        <button class="link-button" @click="() => router.push('/companies')">
                            {{ $t('companies.createHere') }}
                        </button>
                    </p>
                </div>
            </div>

            <!-- STATE B: Dashboard (Company Selected) -->
            <section v-else class="scans-section">
                <!-- Company Header Bar -->
                <div class="company-header-bar">
                    <div class="company-info">
                        <div class="company-name-badge">
                            <font-awesome-icon icon="fa-solid fa-chart-line" />
                            <span class="company-name">{{ selectedCompanyName }}</span>
                        </div>
                        <button class="btn-switch-company" @click="switchCompany">
                            <font-awesome-icon icon="fa-solid fa-arrow-right-arrow-left"/>
                            {{ $t('companies.switchCompany') }}
                        </button>
                    </div>
                </div>

                <!-- Upload Queue -->
                <div class="upload-section">
                    <UploadQueue
                        ref="uploadQueueRef"
                        :company-name="selectedCompanyName"
                        :selected-company-id="selectedGlobalCompany"
                        @scan-completed="refreshScans"
                        @register-completed="handleRegisterCompleted"
                        @company-change="handleUploadQueueCompanyChange"
                    />
                </div>

                <!-- Persistent register notice: which invoices are missing + a
                     button to re-scan ONLY those pages (not the whole register). -->
                <div v-if="registerNotice" class="register-notice">
                    <div class="register-notice__icon">⚠️</div>
                    <div class="register-notice__body">
                        <template v-if="registerNotice.missing_count > 0">
                            <div class="register-notice__title">
                                Липсват {{ registerNotice.missing_count }} фактури от последния регистър
                            </div>
                            <div class="register-notice__nums">{{ (registerNotice.missing_numbers || []).join(', ') }}</div>
                            <div class="register-notice__hint">
                                Натиснете „Досканирай липсващите“ — ще се сканират само страниците с тези номера, не целият регистър.
                            </div>
                        </template>
                        <template v-else>
                            <div class="register-notice__title">Сумата не съвпада с „Сумарно“</div>
                            <div class="register-notice__hint">
                                Получена {{ registerNotice.extracted_with_vat }}, очаквана {{ registerNotice.expected_with_vat }} —
                                възможен дубликат или грешно разчетена сума.
                            </div>
                        </template>
                    </div>
                    <div class="register-notice__actions">
                        <button
                            v-if="registerNotice.missing_count > 0"
                            class="register-notice__btn"
                            :disabled="rescanningMissing"
                            @click="rescanMissingInvoices"
                        >
                            <font-awesome-icon v-if="rescanningMissing" icon="fa-solid fa-spinner" spin />
                            {{ rescanningMissing ? 'Досканиране…' : 'Досканирай липсващите' }}
                        </button>
                        <button class="register-notice__dismiss" @click="dismissRegisterNotice" aria-label="Затвори">×</button>
                    </div>
                </div>

                <!-- Table Container -->
                <div class="table-container">
                    <!-- Company Filter (Top Level) -->
                    <div class="top-company-filter">
                        <div class="filter-item">
                            <label class="filter-label">{{ $t('invoices.company').toUpperCase() }}</label>
                            <CompanySelector
                                v-model="topLevelCompanyId"
                                :class="{ 'active-filter': topLevelCompanyId }"
                                :placeholder="$t('companies.allCompanies')"
                                :show-label="false"
                                @change="handleTopLevelCompanyChange"
                            />
                        </div>
                    </div>

                    <!-- Sort & Export Actions Row - Always Visible -->
                    <div class="actions-row">
                        <div class="type-selector-group">
                            <label class="sort-label">{{ $t('scanDetails.documentType', 'Document Type').toUpperCase() }}</label>
                            <TypeSelector
                                v-model="filterManager.filters.value.type"
                                :class="{ 'active-filter': filterManager.filters.value.type }"
                                :show-label="false"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-export-group">
                            <button :class="{ active: showFilters }" class="btn-filter"
                                    @click="showFilters = !showFilters"
                                    title="Toggle Filters">
                                <font-awesome-icon icon="fa-solid fa-filter"/>
                                <span class="btn-filter-text">{{ $t('common.filter') }}</span>
                            </button>

                            <div class="export-actions">
                                <button class="btn-export-single" @click="openExportAllModal">
                                    <font-awesome-icon icon="fa-solid fa-download"/>
                                    {{ $t('common.exportAll') }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Filters Row -->
                    <div v-show="showFilters" class="filters-row">
                        <div class="filter-item">
                            <label class="filter-label">{{ $t('common.status').toUpperCase() }}</label>
                            <CustomSelect 
                                v-model="filterManager.filters.value.status" 
                                :options="statusOptions"
                                :class="{ 'active-filter': filterManager.filters.value.status }"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item">
                            <label class="filter-label">{{ $t('invoices.vendor').toUpperCase() }}</label>
                            <VendorSelector
                                v-model="filterManager.filters.value.vendorId"
                                :class="{ 'active-filter': filterManager.filters.value.vendorId }"
                                :company-id="topLevelCompanyId"
                                :placeholder="$t('vendors.allVendors')"
                                :show-label="false"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item">
                            <label class="filter-label">{{ $t('invoices.type').toUpperCase() }}</label>
                            <TypeSelector
                                v-model="filterManager.filters.value.type"
                                :class="{ 'active-filter': filterManager.filters.value.type }"
                                :show-label="false"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item">
                            <label class="filter-label">{{ $t('invoices.checked').toUpperCase() }}</label>
                            <CustomSelect 
                                v-model="filterManager.filters.value.isChecked" 
                                :options="checkedOptions"
                                :class="{ 'active-filter': filterManager.filters.value.isChecked !== null && filterManager.filters.value.isChecked !== '' }"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item">
                            <label class="filter-label">{{ $t('invoices.dateFrom').toUpperCase() }}</label>
                            <input
                                v-model="filterManager.filters.value.dateFrom"
                                :class="{ 'active-filter': filterManager.filters.value.dateFrom }"
                                class="filter-input"
                                placeholder="mm/dd/yyyy"
                                type="date"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item">
                            <label class="filter-label">{{ $t('invoices.dateTo').toUpperCase() }}</label>
                            <input
                                v-model="filterManager.filters.value.dateTo"
                                :class="{ 'active-filter': filterManager.filters.value.dateTo }"
                                class="filter-input"
                                placeholder="mm/dd/yyyy"
                                type="date"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item">
                            <label class="filter-label">{{ $t('invoices.uploadDate').toUpperCase() }}</label>
                            <input
                                v-model="filterManager.filters.value.uploadDate"
                                :class="{ 'active-filter': filterManager.filters.value.uploadDate }"
                                class="filter-input"
                                placeholder="mm/dd/yyyy"
                                type="date"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item">
                            <label class="filter-label">{{ $t('common.archived').toUpperCase() }}</label>
                            <CustomSelect 
                                v-model="filterManager.filters.value.showArchived" 
                                :options="archivedOptions"
                                :class="{ 'active-filter': filterManager.filters.value.showArchived }"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item">
                            <label class="filter-label">{{ $t('invoices.paymentMethod').toUpperCase() }}</label>
                            <CustomSelect 
                                v-model="filterManager.filters.value.paymentMethod" 
                                :options="paymentMethodOptions"
                                :class="{ 'active-filter': filterManager.filters.value.paymentMethod }"
                                @change="applyFilters"
                            />
                        </div>

                        <div class="filter-item filter-clear-container">
                            <label class="filter-label">&nbsp;</label>
                            <button class="btn-clear-filters" @click="clearFilters">
                                <font-awesome-icon icon="fa-solid fa-circle-xmark"/>
                                {{ $t('invoices.clearFilters') }}
                            </button>
                        </div>
                    </div>

                    <!-- Select Row with Bulk Actions -->
                    <div class="select-row">
                        <div class="select-all-section">
                            <input id="selectAll" :checked="selection.selectAll.value" type="checkbox"
                                   @change="toggleSelectAll"/>
                            <label for="selectAll">{{ $t('common.selectAll') }} ({{ scans.length }})</label>
                        </div>

                        <!-- Bulk Actions - Show when items selected -->
                        <div v-if="selection.selectedCount.value > 0" class="bulk-actions">
                            <span class="selected-count">{{ selection.selectedCount.value }} {{ $t('common.selected') }}</span>
                            <button class="btn-bulk btn-bulk-export" @click="exportScans()">
                                <font-awesome-icon icon="fa-solid fa-download"/>
                                {{ $t('common.export') }}
                            </button>
                            <button class="btn-bulk btn-bulk-archive" @click="archiveSelected">
                                <font-awesome-icon icon="fa-solid fa-box-archive"/>
                                {{ $t('common.archive') }}
                            </button>
                            <button class="btn-bulk btn-bulk-delete" @click="deleteSelected">
                                <font-awesome-icon icon="fa-solid fa-trash"/>
                                {{ $t('common.delete') }}
                            </button>
                        </div>

                        <!-- No selection: totals centred, scan count on the right -->
                        <template v-else>
                            <div v-if="scansTotalLabel" class="scan-totals-center">{{ scansTotalLabel }}</div>
                            <div class="scan-count">
                                {{ $t('common.showing') }} {{ scans.length }} {{ $t('common.of') }} {{ totalScans }} {{ $t('common.scans') }}
                            </div>
                        </template>
                    </div>

                    <!-- Scans Table -->
                    <ScansTable
                        :loading="loading"
                        :queue-items="queueItems"
                        :scans="scans"
                        :selected-scans="selection.selectedItems.value"
                        :validation-warnings="validationWarnings"
                        :company-name="selectedCompanyName"
                        :enforce-subtotal-validation="authStore.enforceSubtotalValidation"
                        :validate-vat-conflicts="authStore.validateVatConflicts"
                        :empty-title="$t('common.noScansFound')"
                        :empty-message="$t('common.uploadFirstInvoice')"
                        :empty-action-text="$t('common.startScanning')"
                        :sort-by="filterManager.filters.value.sort"
                        :sort-order="filterManager.filters.value.order"
                        @delete="handleDeleteScan"
                        @export="handleExportScan"
                        @toggle-selection="toggleSelect"
                        @toggle-select-all="toggleSelectAll"
                        @empty-action="() => uploadQueueRef?.triggerFileInput()"
                        @cancel-upload="cancelUpload"
                        @type-change="handleTypeChange"
                        @sort-change="handleSortChange"
                    />
                </div>

                <!-- Pagination -->
                <div v-if="pagination.totalPages.value > 1 || true" class="pagination">
                    <div class="pagination-left">
                        <label class="items-per-page-label">{{ $t('common.itemsPerPage') }}</label>
                        <select v-model="pagination.perPage.value" class="items-per-page-select"
                                @change="handlePerPageChange">

                            <option v-if="![12, 300, 500, 1000, 999999].includes(pagination.perPage.value)" :value="pagination.perPage.value">{{ pagination.perPage.value }}</option>
                            <option :value="12">12</option>
                            <option :value="300">300</option>
                            <option :value="500">500</option>
                            <option :value="1000">1000</option>
                            <option :value="999999">{{ $t('common.all') }}</option>
                        </select>
                    </div>

                    <div class="pagination-center">
                        <button
                            :disabled="pagination.isFirstPage.value"
                            class="pagination-btn"
                            @click="goToPage(pagination.currentPage.value - 1)"
                        >
                            {{ $t('common.previous') }}
                        </button>

                        <div class="pagination-numbers">
                            <button
                                v-for="page in pagination.visiblePages.value"
                                :key="page"
                                :class="['pagination-number', { active: page === pagination.currentPage.value }]"
                                @click="goToPage(page)"
                            >
                                {{ page }}
                            </button>
                        </div>

                        <button
                            :disabled="pagination.isLastPage.value"
                            class="pagination-btn"
                            @click="goToPage(pagination.currentPage.value + 1)"
                        >
                            {{ $t('common.next') }}
                        </button>
                    </div>

                    <div class="pagination-right"></div>
                </div>
            </section>

            <!-- QR Code Section -->
            <section class="qr-code-section">
                <div class="qr-code-card">
                    <img v-if="qrCodeDataUrl" :src="qrCodeDataUrl" alt="QR Code" class="qr-code-image"/>
                    <div v-else class="qr-code-placeholder">
                        <div class="spinner"></div>
                        <p>Loading QR Code...</p>
                    </div>
                </div>
                <div class="qr-info">
                    <h3 class="qr-title">{{ $t('common.scanToAccessMobile') }}</h3>
                    <p class="qr-description">{{ $t('common.scanQRDescription') }}</p>
                </div>
            </section>

            <!-- Export Modal -->
            <ExportModal
                v-if="showExportModal"
                :invoice-ids="exportIds"
                :company-name="selectedCompanyName"
                :company-id="selectedGlobalCompany"
                @close="showExportModal = false"
                @exported="handleExported"
                @updated="handleUpdated"
            />

            <!-- Bulk Delete Confirmation Modal -->
            <div v-if="showBulkDeleteModal" class="modal-overlay" @click="showBulkDeleteModal = false">
                <div class="modal-content" @click.stop>
                    <div class="modal-header">
                        <h3>{{ $t('invoices.bulkDeleteConfirm', `Are you sure you want to delete ${selection.selectedCount.value} scan(s)?`) }}</h3>
                        <button @click="showBulkDeleteModal = false" class="btn btn-icon">
                            <font-awesome-icon icon="fa-solid fa-xmark" />
                        </button>
                    </div>
                    <div class="modal-actions">
                        <button @click="executeBulkDelete" class="btn-confirm">{{ $t('common.yes', 'Yes') }}</button>
                        <button @click="showBulkDeleteModal = false" class="btn-cancel">{{ $t('common.no', 'No') }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, computed, onMounted, watch} from 'vue';
import {useRouter, useRoute} from 'vue-router';
import {useI18n} from 'vue-i18n';
import {useScanStore} from '@/stores/scan';
import {useAuthStore} from '@/stores/auth';
import {useUiStore} from '@/stores/ui';
import {useCompanies} from '@/composables/useCompanyVendor';
import {usePagination} from '@/composables/usePagination';
import {validateCompanyOnInvoice} from '@/services/exportValidationService';
import {useFilters} from '@/composables/useFilters';
import {useSelection} from '@/composables/useSelection';
import {useConfirmDialog} from '@/composables/useConfirmDialog';
import Welcome from '@/pages/Welcome.vue';
import UploadQueue from '@/components/scanner/UploadQueue.vue';
import CompanySelector from '@/components/selectors/CompanySelector.vue';
import VendorSelector from '@/components/selectors/VendorSelector.vue';
import TypeSelector from '@/components/selectors/TypeSelector.vue';
import CustomSelect from '@/components/CustomSelect.vue';
import ScansTable from '@/components/scanner/ScansTable.vue';
import ExportModal from '@/components/scanner/ExportModal.vue';
import api from '@/utils/api';

const router = useRouter();
const route = useRoute();
const {t} = useI18n();
const scanStore = useScanStore();
const authStore = useAuthStore();
const uiStore = useUiStore();
const {companies: companiesList, loadCompanies: loadCompaniesList} = useCompanies();
const {confirmBulkDelete, confirmDelete} = useConfirmDialog();

// Options for custom selects
const statusOptions = computed(() => [
    {value: '', label: t('invoices.allStatuses')},
    {value: 'completed', label: t('invoices.statusCompleted')},
    {value: 'processing', label: t('invoices.statusProcessing')},
    {value: 'failed', label: t('invoices.statusFailed')},
    {value: 'pending', label: t('invoices.statusPending')}
]);

const checkedOptions = computed(() => [
    {value: null, label: t('invoices.allInvoices')},
    {value: true, label: t('invoices.checkedOnly')},
    {value: false, label: t('invoices.uncheckedOnly')}
]);

const archivedOptions = computed(() => [
    {value: false, label: t('common.dontShow')},
    {value: true, label: t('common.show')}
]);

const paymentMethodOptions = computed(() => [
    {value: '', label: t('invoices.allPaymentMethods')},
    {value: 'Bank Transfer', label: t('paymentMethods.bank_transfer')},
    {value: 'Cash', label: t('paymentMethods.cash')},
    {value: 'Credit Card', label: t('paymentMethods.credit_card')},
    {value: 'Check', label: t('paymentMethods.check')},
    {value: 'Online Payment', label: t('paymentMethods.online_payment')},
    {value: 'Deferred Payment', label: t('paymentMethods.deferred_payment')},
    {value: 'Partial Payment', label: t('paymentMethods.partial_payment')},
    {value: 'Other', label: t('paymentMethods.other')}
]);

// Check if user is authenticated using auth store
const isAuthenticated = computed(() => authStore.isAuthenticated);

// Pagination
const pagination = usePagination({
    initialPage: parseInt(route.query.page) || 1,
    perPage: 999999 // Show all invoices by default
});

// Filters
const defaultFilters = {
    page: 1,
    status: '',
    sort: '',
    order: 'desc',
    search: '',
    company: '',
    companyId: null,
    vendorId: null,
    type: null,
    isChecked: null,
    dateFrom: '',
    dateTo: '',
    uploadDate: '',
    paymentMethod: '',
    showArchived: false  // Default: don't show archived invoices
};

const filterManager = useFilters({
    defaultFilters,
    onFilterChange: (newFilters, oldFilters) => {
        // Only reset page if filters other than 'page' changed
        const filtersChanged = Object.keys(newFilters).some(key => {
            if (key === 'page') return false; // Ignore page changes
            return newFilters[key] !== oldFilters?.[key];
        });

        if (filtersChanged) {
            pagination.resetPage();
        }
        loadScans();
    }
});

// Selection
const scans = computed(() => scanStore.scans);
const selection = useSelection({
    items: scans,
    idKey: 'id'
});

// Refs
const uploadQueueRef = ref(null);
const storedCompanyId = localStorage.getItem('selectedCompanyId');
const selectedGlobalCompany = ref(storedCompanyId ? parseInt(storedCompanyId) : null);
const tempCompanyId = ref(storedCompanyId ? parseInt(storedCompanyId) : null);
const topLevelCompanyId = ref(null); // Separate ref for top-level company filter
const loading = ref(true);
const showFilters = ref(false);
const showExportModal = ref(false);
const showBulkDeleteModal = ref(false);
const qrCodeDataUrl = ref(null);
const companies = ref([]);
const queueItems = ref([]);
const selectedScans = ref([]);
const exportIds = ref([]);
const validationWarnings = ref([]);
// Persistent notice about the latest register import: which invoices are missing
// (holes in the consecutive № sequence) or a total that disagrees with "Сумарно".
const registerNotice = ref(null);
const rescanningMissing = ref(false);
const DISMISSED_REGISTER_KEY = 'dismissedRegisterNoticeId';
let searchTimeout = null;

// Computed
const totalScans = computed(() => scanStore.pagination?.total || 0);

// Totals of all displayed invoices (subtotal without VAT, VAT, grand total),
// grouped by currency — shown inline next to the scan count so the "Сумарно" is
// visible without scrolling to the bottom summary.
const scansTotalLabel = computed(() => {
    const totals = {};
    scans.value.forEach(s => {
        const c = s.currency || 'BGN';
        if (!totals[c]) totals[c] = { subtotal: 0, vat: 0, grand: 0 };
        totals[c].subtotal += parseFloat(s.subtotal) || 0;
        totals[c].vat += parseFloat(s.tax_amount) || 0;
        totals[c].grand += parseFloat(s.grand_total) || 0;
    });
    const fmt = (v) => v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return Object.entries(totals)
        .filter(([, t]) => t.grand > 0)
        .map(([c, t]) => `Без ДДС: ${fmt(t.subtotal)} · ДДС: ${fmt(t.vat)} · С ДДС: ${fmt(t.grand)} ${c}`)
        .join('  |  ');
});

const selectedCompanyName = computed(() => {
    if (!selectedGlobalCompany.value || !companiesList.value) return '';
    const company = companiesList.value.find(c => c.id === selectedGlobalCompany.value);
    return company ? company.name : '';
});

const selectedCompanyData = computed(() => {
    if (!selectedGlobalCompany.value || !companiesList.value) return null;
    return companiesList.value.find(c => c.id === selectedGlobalCompany.value) || null;
});

// Sync pagination with total items from store
watch(totalScans, (newTotal) => {
    pagination.setTotalItems(newTotal);
});

// Sync currentPage from pagination to route
watch(() => pagination.currentPage.value, (newPage) => {
    if (newPage !== parseInt(route.query.page)) {
        filterManager.setFilter('page', newPage);
        filterManager.updateUrlParams();
    }
});

// Update select all state when scans change
watch(scans, () => {
    selection.updateSelectAllState();
    // Re-validate scans whenever they change
    validateLoadedScans();
}, {deep: true});

// Watch for changes in company validation settings
watch(companiesList, () => {
    // Re-validate when company data changes
    validateLoadedScans();
}, { deep: true });

// Watch for changes in user settings (per-user validation toggles)
watch(() => authStore.settings, () => {
    validateLoadedScans();
}, { deep: true });

// Watch for selected company changes
watch(selectedGlobalCompany, () => {
    // Re-validate when switching companies
    validateLoadedScans();
});

// Sync top-level company selector with filter's companyId
watch(() => filterManager.filters.value.companyId, (newCompanyId) => {
    topLevelCompanyId.value = newCompanyId;
});

// Clear selection when filters change
watch(() => [
    filterManager.filters.value.type,
    filterManager.filters.value.status,
    filterManager.filters.value.companyId,
    filterManager.filters.value.vendorId,
    filterManager.filters.value.isChecked,
    filterManager.filters.value.dateFrom,
    filterManager.filters.value.dateTo,
    filterManager.filters.value.uploadDate,
    filterManager.filters.value.paymentMethod,
    filterManager.filters.value.search
], () => {
    selection.clearSelection();
});

// Watch for company deletions - clear selection if selected company is deleted
watch(companiesList, (newCompanies) => {
    if (selectedGlobalCompany.value && newCompanies) {
        const companyExists = newCompanies.find(c => c.id === selectedGlobalCompany.value);
        if (!companyExists) {
            // Selected company was deleted, clear it
            selectedGlobalCompany.value = null;
            tempCompanyId.value = null;
            localStorage.removeItem('selectedCompanyId');
            filterManager.setFilter('companyId', null);
        }
    }
}, { deep: true });

// Initialize top-level company from filter
topLevelCompanyId.value = filterManager.filters.value.companyId;

const loadScans = async () => {
    // Don't load scans if no company is selected
    if (!selectedGlobalCompany.value) {
        loading.value = false;
        return;
    }

    loading.value = true;
    try {
        const params = {
            ...pagination.getPaginationParams(),
            ...filterManager.getApiParams(),
            company_id: selectedGlobalCompany.value // Enforce company filter
        };

        // Remove show_archived from params as it's not used by backend
        delete params.show_archived;

        console.log('Loading scans with params:', params);

        // Exclude archived invoices from dashboard by default
        // Only include them if the user explicitly chooses to show them
        if (!filterManager.filters.value.showArchived) {
            params.exclude_status = 'archived';
        }

        await scanStore.fetchScans(params);
        
        // Validate loaded scans against company
        validateLoadedScans();
    } catch (error) {
        console.error('Failed to load scans:', error);
    } finally {
        loading.value = false;
    }
};

const validateLoadedScans = () => {
    // Clear previous warnings
    validationWarnings.value = [];
    
    console.log('🚀 validateLoadedScans called');
    console.log('🚀 selectedGlobalCompany:', selectedGlobalCompany.value);
    console.log('🚀 companiesList:', companiesList.value);
    
    if (!selectedGlobalCompany.value) {
        console.log('❌ No company selected, skipping validation');
        return;
    }
    
    // Find the selected company
    const company = companiesList.value.find(c => c.id === selectedGlobalCompany.value);
    
    console.log('🚀 Found company:', company);
    console.log('🚀 Validation enabled?', authStore.validateCompanyOnScan);
    
    if (!company || !authStore.validateCompanyOnScan) {
        console.log('⏭️ Validation skipped:', { 
            hasCompany: !!company, 
            companyName: company?.name,
            validationEnabled: authStore.validateCompanyOnScan 
        });
        return;
    }
    
    console.log('✅ Starting validation for company:', company.name);
    console.log('✅ Company data:', { 
        name: company.name, 
        vat_number: company.vat_number,
        validate_company_on_scan: authStore.validateCompanyOnScan 
    });
    
    // Check all loaded scans
    const allScans = scanStore.scans || [];

    allScans.forEach((scan) => {
        // Check if invoice data is in scan.result or directly on scan object
        const invoiceData = scan.result || scan;

        if (invoiceData && (invoiceData.buyer_name || invoiceData.seller_name)) {
            const validation = validateCompanyOnInvoice({ result: invoiceData }, company, { enabled: true });

            if (!validation.isValid) {
                const invoiceNum = validation.invoiceNumber || scan.invoice_number || 'Unknown';

                // Add to warnings array
                validationWarnings.value.push({
                    id: scan.id,
                    invoiceNumber: invoiceNum
                });
            }
        }
    });

    if (validationWarnings.value.length > 0) {
        console.warn(`⚠️ Found ${validationWarnings.value.length} invoices without company "${company.name}"`);
        console.warn('⚠️ These invoices will show in the warning banner');
    } else {
        console.log('✅ All invoices contain company "${company.name}"');
    }
}

const handleCompanySelection = (company) => {
    if (company && company.id) {
        selectedGlobalCompany.value = company.id;
        // Set the company filter permanently
        filterManager.setFilter('companyId', company.id);
        // Load scans for this company
        loadScans();
        // Store in localStorage for persistence
        localStorage.setItem('selectedCompanyId', company.id.toString());
    }
};

const handleUploadQueueCompanyChange = (companyId) => {
    if (companyId) {
        selectedGlobalCompany.value = companyId;
        tempCompanyId.value = companyId;
        // Set the company filter
        filterManager.setFilter('companyId', companyId);
        // Load scans for this company
        loadScans();
        // Store in localStorage for persistence
        localStorage.setItem('selectedCompanyId', companyId.toString());
    } else {
        // If company is cleared, switch back to selection screen
        switchCompany();
    }
};

const switchCompany = () => {
    router.push('/companies');
};

const refreshScans = async () => {
    // Add a small delay to ensure backend has processed the scan
    await new Promise(resolve => setTimeout(resolve, 500));
    await loadScans();
};

const applyFilters = () => {
    filterManager.applyFilters();
};

const clearFilters = () => {
    filterManager.clearFilters();
};

const handleSearchInput = () => {
    if (searchTimeout) clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        applyFilters();
    }, 500);
};

const handleCompanyChange = () => {
    filterManager.setFilter('vendorId', null);
    applyFilters();
};

const handleTopLevelCompanyChange = (company) => {
    const companyId = company?.id || null;
    filterManager.setFilter('companyId', companyId);
    filterManager.setFilter('vendorId', null); // Reset vendor when company changes
    applyFilters();
};

const handleSortChange = ({ sortBy, sortOrder }) => {
    filterManager.setFilter('sort', sortBy);
    filterManager.setFilter('order', sortOrder);
    applyFilters();
};

const goToPage = (page) => {
    if (pagination.goToPage(page)) {
        loadScans();
        window.scrollTo({top: 0, behavior: 'smooth'});
    }
};

const handlePerPageChange = () => {
    pagination.setPerPage(pagination.perPage.value);
    loadScans();
};

// After a register import, always show ALL invoices (these imports create many rows).
const handleRegisterCompleted = async () => {
    pagination.setPerPage(999999);
    await refreshScans();
    // A fresh import should always surface its notice, even if an earlier register
    // was dismissed.
    localStorage.removeItem(DISMISSED_REGISTER_KEY);
    await fetchRegisterIssues();
};

// Load the latest register import's unresolved issues (missing invoices / total
// mismatch) so the notice persists across reloads, not just as a one-off alert.
const fetchRegisterIssues = async () => {
    try {
        const { data } = await api.get('/api/invoices/register-issues');
        const n = data?.notice;
        if (!n) {
            registerNotice.value = null;
            return;
        }
        if (localStorage.getItem(DISMISSED_REGISTER_KEY) === String(n.register_id)) {
            registerNotice.value = null;
            return;
        }
        registerNotice.value = n;
    } catch (e) {
        // Non-critical — leave any existing notice untouched.
    }
};

const dismissRegisterNotice = () => {
    if (registerNotice.value?.register_id) {
        localStorage.setItem(DISMISSED_REGISTER_KEY, String(registerNotice.value.register_id));
    }
    registerNotice.value = null;
};

// Re-scan ONLY the pages holding the still-missing invoices and add them, without
// re-processing the whole register.
const rescanMissingInvoices = async () => {
    const id = registerNotice.value?.register_id;
    if (!id || rescanningMissing.value) return;

    rescanningMissing.value = true;
    try {
        const { data } = await api.post(`/api/invoices/${id}/rescan-missing`);
        await refreshScans();

        if ((data?.missing_count ?? 0) > 0) {
            // Still some missing → refresh the notice with the new (shorter) list.
            registerNotice.value = {
                ...registerNotice.value,
                missing_numbers: data.missing_numbers || [],
                missing_count: data.missing_count,
                child_count: data.child_count ?? registerNotice.value.child_count,
            };
            alert(`Досканирани ${data.recovered} фактури. Все още липсват ${data.missing_count}:\n${(data.missing_numbers || []).join(', ')}`);
        } else {
            registerNotice.value = null;
            alert(data?.recovered > 0
                ? `Готово: досканирани ${data.recovered} фактури. Вече няма липсващи.`
                : 'Няма липсващи фактури.');
        }
    } catch (e) {
        alert(e?.response?.data?.message || 'Досканирането се провали. Опитайте пак.');
    } finally {
        rescanningMissing.value = false;
    }
};

const toggleSelect = (scanId) => {
    selection.toggleSelect(scanId);
};

const toggleSelectAll = () => {
    selection.toggleSelectAll();
};

const exportScans = (format) => {
    if (selection.selectedCount.value === 0) {
        alert('Please select at least one scan to export');
        return;
    }
    const selectedSet = new Set(selection.selectedItems.value);
    exportIds.value = scans.value.map(s => s.id).filter(id => selectedSet.has(id));
    showExportModal.value = true;
};

const exportAll = async (format) => {
    // For TXT format, show modal to select NAP type
    if (format === 'txt') {
        // Show modal with TXT options
        selectedScans.value = scans.value.map(scan => scan.id);
        showExportModal.value = true;
        return;
    }

    // For other formats, export directly
    const formatMap = {
        'original': null,
        'pdf': 'pdf',
        'xlsx': 'html'
    };

    const apiFormat = formatMap[format];

    if (format === 'original') {
        alert('Downloading original files for all scans...');
        // TODO: Implement original files bulk download
        return;
    }

    if (!apiFormat) {
        console.error('Unknown format:', format);
        return;
    }

    const invoiceIds = scans.value.map(scan => scan.id);

    if (invoiceIds.length === 0) {
        alert('No scans to export');
        return;
    }

    try {
        const response = await api.post('/api/scans/export/bulk', {
            invoice_ids: invoiceIds,
            format: apiFormat
        }, {
            responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const filename = `invoices_${timestamp}.zip`;

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Export failed:', error);
        alert('Failed to export. Please try again.');
    }
};

const downloadAllPdf = async () => {
    const invoiceIds = scans.value.map(scan => scan.id);
    if (invoiceIds.length === 0) {
        alert('No scans to export');
        return;
    }

    try {
        const response = await api.post('/api/scans/export/bulk', {
            invoice_ids: invoiceIds,
            format: 'pdf'
        }, {
            responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        link.setAttribute('download', `invoices_${timestamp}.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Export failed:', error);
        alert('Failed to export PDF. Please try again.');
    }
};

const downloadAllNapPurchases = async () => {
    const invoiceIds = scans.value.map(scan => scan.id);
    if (invoiceIds.length === 0) {
        alert('No scans to export');
        return;
    }

    try {
        const response = await api.post('/api/scans/export/bulk', {
            invoice_ids: invoiceIds,
            format: 'txt_pokupki'
        }, {
            responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        link.setAttribute('download', `POKUPKI_${timestamp}.txt`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Export failed:', error);
        alert('Failed to export NAP Purchases. Please try again.');
    }
};

const downloadAllNapSales = async () => {
    const invoiceIds = scans.value.map(scan => scan.id);
    if (invoiceIds.length === 0) {
        alert('No scans to export');
        return;
    }

    try {
        const response = await api.post('/api/scans/export/bulk', {
            invoice_ids: invoiceIds,
            format: 'txt_prodagbi'
        }, {
            responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        link.setAttribute('download', `PRODAGBI_${timestamp}.txt`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Export failed:', error);
        alert('Failed to export NAP Sales. Please try again.');
    }
};

const downloadAllHtml = async () => {
    const invoiceIds = scans.value.map(scan => scan.id);
    if (invoiceIds.length === 0) {
        alert('No scans to export');
        return;
    }

    try {
        const response = await api.post('/api/scans/export/bulk', {
            invoice_ids: invoiceIds,
            format: 'html'
        }, {
            responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        link.setAttribute('download', `invoices_${timestamp}.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Export failed:', error);
        alert('Failed to export HTML. Please try again.');
    }
};

const downloadAllOriginal = () => {
    alert('Bulk download of original files is not yet implemented. Please select individual scans and use the export modal.');
};

const openExportAllModal = () => {
    exportIds.value = scans.value.map(scan => scan.id);
    showExportModal.value = true;
};

const handleExportScan = (scanIds) => {
    // Set export IDs without modifying selection state
    if (Array.isArray(scanIds)) {
        exportIds.value = scanIds;
    } else {
        exportIds.value = [scanIds];
    }
    showExportModal.value = true;
};

const handleDeleteScan = async (scanId) => {
    console.log('Delete clicked for scan:', scanId);

    try {
        const result = await scanStore.deleteScan(scanId);
        if (result.success === false) {
            throw new Error(result.error || 'Failed to delete');
        }
        await loadScans();
        // Refresh subscription to update usage counter
        console.log('Refreshing subscription after deletion...');
        await authStore.fetchUser();
        console.log('Subscription refreshed. New count:', authStore.subscription?.scans_used);
    } catch (error) {
        console.error('Failed to delete scan:', error);
        alert('Failed to delete scan. Please try again.');
    }
};

const handleTypeChange = async ({scanId, type}) => {
    try {
        await api.put(`/api/invoices/${scanId}`, {type});
        // Reload scans to reflect the change
        await loadScans();
    } catch (error) {
        console.error('Failed to update invoice type:', error);
        alert('Failed to update invoice type. Please try again.');
    }
};

const deleteSelected = async () => {
    if (selection.selectedCount.value === 0) {
        return;
    }

    showBulkDeleteModal.value = true;
};

const executeBulkDelete = async () => {
    showBulkDeleteModal.value = false;

    try {
        // Delete in bulk (one request per chunk) instead of one request per scan.
        const ids = [...selection.selectedItems.value];
        const chunkSize = 300;
        for (let i = 0; i < ids.length; i += chunkSize) {
            const chunk = ids.slice(i, i + chunkSize);
            await api.post('/api/invoices/bulk-delete', { invoice_ids: chunk });
        }

        // Clear selection and reload
        selection.clearSelection();
        await loadScans();
    } catch (error) {
        console.error('Failed to delete scans:', error);
        alert('Failed to delete some scans. Please try again.');
    }
};

const archiveSelected = async () => {
    if (selection.selectedCount.value === 0) {
        return;
    }

    if (!confirm(`${t('common.archiveConfirm')} ${selection.selectedCount.value} ${t('common.invoices')}?`)) {
        return;
    }

    try {
        // Archive each selected scan
        for (const scanId of selection.selectedItems.value) {
            await api.post(`/api/invoices/${scanId}/archive`);
        }

        // Clear selection and reload
        selection.clearSelection();
        await loadScans();
    } catch (error) {
        console.error('Failed to archive scans:', error);
        alert('Failed to archive some scans. Please try again.');
    }
};

const updateQueueItems = (items) => {
    queueItems.value = items;
};

const cancelUpload = (itemId) => {
    // The upload queue component will handle the actual cancellation
    queueItems.value = queueItems.value.filter(item => item.id !== itemId);
};

const handleExported = () => {
    // Don't clear selection after export
    showExportModal.value = false;
};

const handleUpdated = async () => {
    // Refresh scans list after changes in ExportModal
    await loadScans();
};

const loadQrCode = async () => {
    if (!qrCodeDataUrl.value) {
        try {
            const response = await api.get('/api/invoice/qr/show', {
                responseType: 'blob'
            });
            qrCodeDataUrl.value = URL.createObjectURL(response.data);
        } catch (error) {
            console.error('Failed to load QR code:', error);
        }
    }
};

onMounted(async () => {
    // Fetch user data first if not already loaded
    if (!authStore.user) {
        await authStore.fetchUser();
    }

    await loadCompaniesList();
    loadQrCode();

    // Surface any unresolved register issues (missing invoices / total mismatch).
    // Must run before the company branches below, which can return early.
    fetchRegisterIssues();

    // Check for company ID in URL query parameter first
    const urlCompanyId = route.query.companyId ? parseInt(route.query.companyId) : null;

    if (urlCompanyId) {
        // Verify company exists
        const companyExists = companiesList.value.some(c => c.id === urlCompanyId);
        if (companyExists) {
            selectedGlobalCompany.value = urlCompanyId;
            tempCompanyId.value = urlCompanyId;
            localStorage.setItem('selectedCompanyId', urlCompanyId.toString());
            filterManager.setFilter('companyId', urlCompanyId);
            await loadScans();
            return;
        }
    }

    // Check if stored company ID is still valid
    if (selectedGlobalCompany.value && companiesList.value) {
        const companyExists = companiesList.value.find(c => c.id === selectedGlobalCompany.value);
        if (companyExists) {
            filterManager.setFilter('companyId', selectedGlobalCompany.value);
            await loadScans();
        } else {
            // Stored company no longer exists, clear it
            selectedGlobalCompany.value = null;
            tempCompanyId.value = null;
            localStorage.removeItem('selectedCompanyId');
        }
    }
});

// Watch for URL query parameter changes to switch company
watch(() => route.query.companyId, async (newCompanyId) => {
    if (newCompanyId) {
        const companyId = parseInt(newCompanyId);
        const companyExists = companiesList.value.some(c => c.id === companyId);
        if (companyExists && companyId !== selectedGlobalCompany.value) {
            selectedGlobalCompany.value = companyId;
            tempCompanyId.value = companyId;
            localStorage.setItem('selectedCompanyId', companyId.toString());
            filterManager.setFilter('companyId', companyId);
            // Clear current selections
            selection.clearSelection();
            await loadScans();
        }
    }
});

// Keyboard shortcuts for bulk delete modal
const handleBulkDeleteKeydown = (event) => {
    if (showBulkDeleteModal.value) {
        if (event.key === 'Escape') {
            showBulkDeleteModal.value = false;
        } else if (event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            executeBulkDelete();
        }
    }
};

onMounted(() => {
    window.addEventListener('keydown', handleBulkDeleteKeydown);
});

watch(() => showBulkDeleteModal.value, (newVal) => {
    if (!newVal) {
        // Cleanup if needed
    }
});
</script>

<style scoped>
.home-page {
    min-height: 100vh;
    background: var(--bg-gray);
    padding: 24px 0;
}

.page-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 24px;
}

/* Company Selection Screen (STATE A) */
.company-selection-screen {
    min-height: 80vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
}

.selection-card {
    background: white;
    border-radius: 20px;
    padding: 60px 80px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    text-align: center;
    max-width: 600px;
    width: 100%;
}

.selection-icon {
    margin-bottom: 32px;
}

.selection-title {
    font-size: 32px;
    font-weight: 700;
    text-wrap: pretty;
    word-break: break-all;
    color: var(--text-primary);
    margin: 0 0 12px 0;
}

.selection-subtitle {
    font-size: 18px;
    color: var(--text-secondary);
    margin: 0 0 40px 0;
}

.selection-selector {
    margin-bottom: 24px;
}

.selection-hint {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0;
}

.link-button {
    background: none;
    border: none;
    color: var(--primary);
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
    padding: 0;
    font-size: 14px;
}

.link-button:hover {
    color: #0284c7;
}

/* Company Header Bar (STATE B) */
.company-header-bar {
    background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%);
    border-radius: 12px;
    padding: 20px 24px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(14, 165, 233, 0.2);
}

.company-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.company-name-badge {
    display: flex;
    align-items: center;
    gap: 12px;
    color: white;
}

.company-name-badge svg {
    flex-shrink: 0;
}

.company-name {
    font-size: 20px;
    font-weight: 700;
    color: white;
}

.btn-switch-company {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 20px;
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    color: white;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    backdrop-filter: blur(10px);
}

.btn-switch-company:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateX(-2px);
}

.btn-switch-company svg {
    flex-shrink: 0;
    display: block;
    vertical-align: middle;
}

/* Upload Section */
.upload-section {
    margin-bottom: 32px;
}

/* QR Code Section */
.qr-code-section {
    display: flex;
    align-items: center;
    gap: 32px;
    background: white;
    border-radius: 16px;
    padding: 32px;
    margin-bottom: 32px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.qr-code-card {
    flex-shrink: 0;
    width: 200px;
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: 2px solid var(--border);
    border-radius: 12px;
    padding: 16px;
}

.qr-code-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.qr-code-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: var(--text-secondary);
}

.qr-code-placeholder p {
    font-size: 13px;
    margin: 0;
}

.spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--border);
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.qr-info {
    flex: 1;
}

.qr-title {
    font-size: 20px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 8px 0;
}

.qr-description {
    font-size: 15px;
    color: var(--text-secondary);
    margin: 0;
    line-height: 1.6;
}

/* Scans Section */
.scans-section {
    margin-top: 32px;
    margin-bottom: 48px;
}

.table-container {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 32px;
}

/* Top-level Company Filter */
.top-company-filter {
    padding: 20px 24px;
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    border-bottom: 1px solid var(--border);
}

.top-company-filter .filter-item {
    max-width: 300px;
}

/* Table Header */
.table-header {
    padding: 20px 24px;
    border-bottom: 2px solid var(--border);
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.header-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
}

.btn-manual-entry,
.btn-companies,
.btn-filter {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 20px;
    background: white;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    height: 45px;
}

.btn-manual-entry {
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    color: white;
    border-color: #8b5cf6;
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.25);
}

.btn-manual-entry:hover {
    background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.35);
}

.btn-filter {
    background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
    color: white;
    border: none;
    box-shadow: 0 4px 12px rgba(14, 165, 233, 0.25);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.btn-filter svg {
    display: block;
    flex-shrink: 0;
}

.btn-filter-text {
    font-size: 14px;
    font-weight: 600;
    color: white;
}

.btn-filter.active {
    background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
    box-shadow: 0 4px 12px rgba(14, 165, 233, 0.35);
}

.btn-companies:hover {
    border-color: var(--primary);
    color: var(--primary);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(14, 165, 233, 0.15);
}

.btn-filter:hover {
    background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(14, 165, 233, 0.35);
}

/* Filters Row */
.filters-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 18px;
    padding: 24px;
    border-bottom: 2px solid var(--border);
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
}

.filter-item {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.filter-label {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.8px;
}

/* Hide the standalone "Filter:" label if it exists */
.filters-row > .filter-label {
    display: none;
}

.filter-select,
.filter-input {
    padding: 10px 14px;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 14px;
    color: var(--text-primary);
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-height: 42px;
}

.filter-select.active-filter,
.filter-input.active-filter {
    border: 2px solid #0ea5e9;
    background: rgba(14, 165, 233, 0.05);
}

.filter-clear-container {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
}

.btn-clear-filters {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 10px 18px;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-secondary);
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    min-height: 42px;
}

.btn-clear-filters:hover {
    border-color: #ef4444;
    color: white;
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-clear-filters svg {
    flex-shrink: 0;
    font-size: 16px;
}

.filter-select:hover,
.filter-input:hover {
    border-color: var(--gray-light);
}

.filter-select:focus,
.filter-input:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.filter-select option {
    background: white;
    color: var(--text-primary);
}

.filter-select option:checked,
.filter-select option:hover,
.filter-select option:focus {
    background: var(--primary-light) !important;
    color: var(--text-primary) !important;
}

/* Actions Row */
.actions-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 2px solid var(--border);
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    gap: 16px;
}

.sort-group {
    display: flex;
    align-items: center;
    gap: 14px;
}

.type-selector-group {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
}

.filter-export-group {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-left: auto;
}

.actions-row .btn-filter {
    margin-left: 0;
}

.sort-label {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.8px;
}

.sort-select {
    padding: 11px 16px;
    border: 2px solid var(--border);
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
    background: white;
    cursor: pointer;
    min-width: 180px;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.sort-select:hover {
    border-color: #0ea5e9;
    box-shadow: 0 2px 8px rgba(14, 165, 233, 0.15);
}

.sort-select:focus {
    outline: none;
    border-color: #0ea5e9;
    box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.15);
}

.export-actions {
    display: flex;
    gap: 12px;
}

.btn-export-single {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 12px 24px;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 700;
    color: white;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    height: 45px;
}

.btn-export-single:hover {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.btn-export {
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-original {
    background: #0ea5e9;
}

.btn-original:hover {
    background: #0284c7;
}

.btn-pdf {
    background: #ef4444;
}

.btn-pdf:hover {
    background: #dc2626;
}

.btn-txt {
    background: #64748b;
}

.btn-txt:hover {
    background: #475569;
}

.btn-nap {
    background: #8b5cf6;
}

.btn-nap:hover {
    background: #7c3aed;
}

.btn-nap-alt {
    background: #ec4899;
}

.btn-nap-alt:hover {
    background: #db2777;
}

.btn-html {
    background: #f59e0b;
}

.btn-html:hover {
    background: #d97706;
}

.btn-xlsx {
    background: #10b981;
}

.btn-xlsx:hover {
    background: #059669;
}

/* Select Row */
.select-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border);
    background: var(--bg-gray);
    min-height: 60px;
    position: relative; /* anchor the centred scan-count/totals */
}

.select-all-section {
    display: flex;
    align-items: center;
    gap: 8px;
}

.select-all-section input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
}

.select-all-section label {
    font-size: 13px;
    color: var(--text-secondary);
    cursor: pointer;
}

.bulk-actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

.selected-count {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    padding: 8px 12px;
    background: var(--white);
    border-radius: 6px;
}

.btn-bulk {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    border: none !important;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-bulk-export {
    background: #10b981 !important;
    background-color: #10b981 !important;
    color: white !important;
    border: none !important;
    outline: none !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.btn-bulk-export:hover {
    background: #059669 !important;
    background-color: #059669 !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

.btn-bulk-archive {
    background: #f59e0b !important;
    background-color: #f59e0b !important;
    color: white !important;
    border: none !important;
    outline: none !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.btn-bulk-archive:hover {
    background: #d97706 !important;
    background-color: #d97706 !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

.btn-bulk-delete {
    background: #ef4444 !important;
    background-color: #ef4444 !important;
    color: white !important;
    border: none !important;
    outline: none !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.btn-bulk-delete:hover {
    background: #dc2626 !important;
    background-color: #dc2626 !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

.scan-count {
    font-size: 13px;
    color: var(--text-secondary);
}

.scan-totals-center {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
    text-align: center;
    font-size: 13px;
    color: var(--text-secondary);
}

/* Pagination */
.pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-top: 32px;
    padding-top: 32px;
    border-top: 1px solid var(--border);
}

.pagination-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
}

.pagination-center {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.pagination-right {
    flex: 1;
}

.items-per-page-label {
    font-size: 14px;
    color: var(--text-secondary);
    font-weight: 500;
}

.items-per-page-select {
    padding: 8px 12px;
    background: white;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 14px;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.2s ease;
}

.items-per-page-select:hover {
    border-color: var(--primary);
}

.items-per-page-select:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.pagination-btn {
    padding: 10px 20px;
    background: white;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
    border-color: var(--primary);
    color: var(--primary);
}

.pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.pagination-numbers {
    display: flex;
    gap: 6px;
}

.pagination-number {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.2s ease;
}

.pagination-number:hover {
    border-color: var(--primary);
    color: var(--primary);
}

.pagination-number.active {
    background: var(--primary);
    border-color: var(--primary);
    color: white;
}

@media (max-width: 768px) {
    .scans-section {
        padding: 12px;
    }

    .qr-code-section {
        flex-direction: column;
        padding: 16px;
    }

    .qr-code-card {
        width: 160px;
        height: 160px;
    }

    .qr-info {
        text-align: center;
    }

    .filters-section {
        flex-direction: column;
    }

    .filters-grid {
        grid-template-columns: 1fr;
    }

    .bulk-actions-bar {
        flex-direction: column;
        gap: 12px;
    }

    .table-header {
        padding: 12px;
    }

    .header-actions {
        width: 100%;
        flex-wrap: wrap;
    }

    .btn-manual-entry,
    .btn-companies,
    .btn-filter {
        flex: 1;
        min-width: calc(50% - 6px);
        padding: 10px 12px;
        font-size: 13px;
        gap: 6px;
        height: auto;
    }

    .filters-row {
        grid-template-columns: 1fr;
        padding: 16px 12px;
        gap: 12px;
    }

    .filters-row .filter-item {
        width: 100%;
    }

    .actions-row {
        flex-direction: column;
        align-items: stretch;
        padding: 12px;
        gap: 12px;
    }

    .filter-export-group {
        display: flex;
        gap: 12px;
        width: 100%;
    }

    .filter-export-group .btn-filter,
    .filter-export-group .export-actions {
        flex: 1 1 50%;
        min-width: 0;
        max-width: 50%;
    }

    .filter-export-group .btn-filter {
        margin-left: 0;
    }

    .filter-export-group .btn-export-single {
        width: 100%;
    }

    .sort-group,
    .type-selector-group {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }

    .sort-label {
        font-size: 10px;
    }

    .sort-select {
        width: 100%;
        min-width: auto;
        padding: 10px 12px;
        font-size: 13px;
    }

    .export-actions {
        width: 100%;
    }

    .btn-export-single {
        width: 100%;
        justify-content: center;
        padding: 10px 16px;
        font-size: 13px;
        height: auto;
    }

    .select-row {
        flex-direction: column;
        align-items: stretch;
        padding: 12px;
        gap: 12px;
    }

    .select-all-section {
        justify-content: center;
    }

    .select-all-section label {
        font-size: 12px;
    }

    .bulk-actions {
        flex-direction: column;
        width: 100%;
        gap: 8px;
    }

    .selected-count {
        text-align: center;
    }

    .btn-bulk {
        width: 100%;
        justify-content: center;
        font-size: 13px;
        padding: 8px 14px;
    }

    .scan-count {
        text-align: center;
        font-size: 12px;
    }

    .scan-totals-center {
        position: static;
        transform: none;
        white-space: normal;
        text-align: center;
        font-size: 12px;
    }

    .bulk-actions-left,
    .bulk-actions-right {
        width: 100%;
        justify-content: center;
    }

    .pagination {
        flex-direction: column;
        gap: 16px;
    }

    .pagination-left,
    .pagination-center {
        width: 100%;
        justify-content: center;
    }

    .pagination-numbers {
        gap: 4px;
    }

    .pagination-number {
        width: 36px;
        height: 36px;
        font-size: 12px;
    }

    .pagination-btn {
        padding: 8px 12px;
        font-size: 12px;
    }

    .items-per-page-label {
        font-size: 12px;
    }

    .items-per-page-select {
        padding: 6px 10px;
        font-size: 12px;
    }
}

/* Bulk Delete Modal */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 12px;
    padding: 24px;
    max-width: 400px;
    width: 90%;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    position: relative;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e2e8f0;
    position: relative;
}

.modal-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
    padding-right: 40px;
}

.modal-header .btn {
    position: absolute;
    top: 0;
    right: 0;
    flex-shrink: 0;
}

.modal-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
}

.btn-confirm,
.btn-cancel {
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-confirm {
    background: #10b981;
    color: white;
}

.btn-confirm:hover {
    background: #059669;
}

.btn-cancel {
    background: #f1f5f9;
    color: #64748b;
}

.btn-cancel:hover {
    background: #e2e8f0;
}

/* Persistent register-missing notice */
.register-notice {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    margin: 0 0 1rem;
    padding: 0.9rem 1rem;
    background: #fef3c7;
    border: 1px solid #fcd34d;
    border-radius: 10px;
    color: #78350f;
}

.register-notice__icon {
    font-size: 1.25rem;
    line-height: 1.4;
}

.register-notice__body {
    flex: 1 1 auto;
    min-width: 0;
}

.register-notice__title {
    font-weight: 700;
    margin-bottom: 0.25rem;
}

.register-notice__nums {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.85rem;
    word-break: break-word;
    background: rgba(255, 255, 255, 0.6);
    border-radius: 6px;
    padding: 0.35rem 0.5rem;
    margin-bottom: 0.4rem;
}

.register-notice__hint {
    font-size: 0.82rem;
    opacity: 0.85;
}

.register-notice__actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 0 0 auto;
    align-self: center; /* vertically centre the button within the tall banner */
}

.register-notice__btn {
    background: #d97706;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 0.5rem 0.85rem;
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    white-space: nowrap;
}

.register-notice__btn:hover:not(:disabled) {
    background: #b45309;
}

.register-notice__btn:disabled {
    opacity: 0.65;
    cursor: default;
}

.register-notice__dismiss {
    background: transparent;
    border: none;
    color: #78350f;
    font-size: 1.4rem;
    line-height: 1;
    cursor: pointer;
    padding: 0 0.25rem;
}
</style>
